import { LoadingOverlay, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { sortByKey } from '@semios/app-platform-common'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { MultiSelect } from 'components/MultiSelect/MultiSelect'
import { translate } from 'i18n/i18n'
import { isEqual } from 'lodash'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { zodResolver } from 'utils/zodResolver'
import type { GroupFormType } from '../_utils/formTypesAndStyles'
import { invalidInputClassName } from '../_utils/formTypesAndStyles'
import { getMultiSelectOptions } from '../_utils/getMultiSelectOptions'
import type { NotificationType } from '../_utils/useRelativeNotification'
import { groupValidationSchema } from '../_utils/validationSchemas'
import { ContactAndGroupSubmitButton } from '../components/ContactAndGroupSubmitButton'
import type { Group } from '../ContactsAndGroupsContext'
import { useGroupsAndContacts } from '../ContactsAndGroupsContext'

export const ModalDrawerCreateOrEditGroup = ({
  opened,
  onClose,
  isEdit,
  selectedGroup,
  setSelectedGroup,
  showRelativeNotification,
}: {
  opened: boolean
  onClose: () => void
  isEdit: boolean
  selectedGroup: Group | null
  setSelectedGroup: Dispatch<SetStateAction<Group | null>>
  showRelativeNotification: ({ type, message }: { type: NotificationType; message: string }) => void
}) => {
  const isWideScreen = useIsWideScreen()
  const notificationFunctionToUse = isWideScreen ? showRelativeNotification : showNotification
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { groups: allGroups, contacts: allContacts, refreshContactsAndGroups } = useGroupsAndContacts()

  const groupMemberOptions = getMultiSelectOptions(
    allContacts.map((contact) => ({
      id: contact.id,
      name: contact.name,
    })),
  )

  const form = useForm<GroupFormType>({
    initialValues: {
      name: '',
      contacts: [],
    },

    validate: zodResolver(groupValidationSchema(allGroups, selectedGroup?.id)),
    validateInputOnBlur: true,
  })

  const getDefaultFormState = () => ({
    name: selectedGroup?.name || '',
    contacts: selectedGroup?.contacts
      ? getMultiSelectOptions(
          selectedGroup.contacts.map((contact) => ({
            id: contact.id,
            name: contact.name,
          })),
        )
      : [],
  })

  useEffect(() => {
    form.setValues(getDefaultFormState())
  }, [selectedGroup, opened])

  const handleOnClose = () => {
    onClose()

    form.reset()

    setSelectedGroup(null)
  }

  const handleCreateGroup = async () => {
    const values = form.values
    const { name, contacts } = values

    setIsSubmitting(true)

    const requestBody: routes.GroupsManagement.Request = {
      groupsToCreate: [
        {
          name,
          contacts: contacts.map((contact) => String(contact.value)),
        },
      ],
    }

    try {
      const response = await apiFetch({
        url: '/groups-management',
        body: requestBody,
      })

      const groupId = Object.keys(response?.data?.groupsToCreate || {})[0]

      if (groupId) {
        notificationFunctionToUse({
          type: 'success',
          message: translate.phrases.banyanApp('Group successfully created'),
        })

        refreshContactsAndGroups()

        handleOnClose()
      } else {
        notificationFunctionToUse({
          type: 'error',
          message: translate.phrases.banyanApp('Failed to create a new group'),
        })
      }
    } catch (error) {
      notificationFunctionToUse({
        type: 'error',
        message: translate.phrases.banyanApp('Failed to create a new group'),
      })
    }

    setIsSubmitting(false)
  }

  const handleEditGroup = async () => {
    const values = form.values
    const { name, contacts } = values
    const contactIds = contacts.map((contact) => String(contact.value))
    const selectedGroupId = selectedGroup?.id

    if (!selectedGroupId) return

    setIsSubmitting(true)

    const requestBody: routes.GroupsManagement.Request = {
      groupsToUpdate: {
        [selectedGroupId]: {
          name,
          contacts: contactIds,
        },
      },
    }

    try {
      const response = await apiFetch({
        url: '/groups-management',
        body: requestBody,
      })

      const editedGroupId = Object.keys(response?.data?.groupsToUpdate || {})[0]

      if (editedGroupId) {
        notificationFunctionToUse({
          type: 'success',
          message: translate.phrases.banyanApp('Group successfully updated'),
        })

        refreshContactsAndGroups()

        handleOnClose()
      } else {
        notificationFunctionToUse({
          type: 'error',
          message: translate.phrases.banyanApp('Failed to update group'),
        })
      }
    } catch (error) {
      notificationFunctionToUse({
        type: 'error',
        message: translate.phrases.banyanApp('Failed to update group'),
      })
    }

    setIsSubmitting(false)
  }

  const groupMembersNumber = form.getInputProps('contacts').value.length || 0
  const horizontalGap = isWideScreen ? 50 : 10
  const isFormDirty = !isEqual(form.values, getDefaultFormState())

  return (
    <ModalDrawer
      title={
        isEdit ? translate.phrases.banyanApp('Group Details') : translate.phrases.banyanApp('Create Group')
      }
      opened={opened}
      onClose={handleOnClose}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX + 300}
      size="lg"
    >
      <LoadingOverlay visible={isSubmitting} />

      <div css={{ paddingBottom: 'calc(90px + env(safe-area-inset-bottom))' }}>
        <form onSubmit={form.onSubmit(() => (isEdit ? handleEditGroup() : handleCreateGroup()))}>
          <div css={{ padding: `15px ${horizontalGap}px` }}>
            <TextInput
              label={translate.phrases.banyanApp('Group Name')}
              {...form.getInputProps('name')}
              classNames={{
                input: form.errors.name ? invalidInputClassName : undefined,
              }}
            />
            <div>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: 20,
                  marginBottom: '3px',
                  fontSize: 16,
                  fontWeight: 500,
                }}
              >
                <div>{translate.phrases.banyanApp('Add Group Member(s)')}</div>
                <div css={{ marginLeft: 5 }}>{`(${groupMembersNumber})`}</div>
              </div>
              <MultiSelect
                data={groupMemberOptions}
                selectedData={form.getInputProps('contacts').value.sort(sortByKey('label'))}
                placeholder=""
                {...form.getInputProps('contacts')}
              />
            </div>
          </div>
          <ContactAndGroupSubmitButton
            label={
              isEdit
                ? translate.phrases.banyanApp('Update Group')
                : translate.phrases.banyanApp('Create Group')
            }
            disabled={isEdit && !isFormDirty}
          />
        </form>
      </div>
    </ModalDrawer>
  )
}
